import React, { useState, useEffect } from 'react';
import { contactAPI } from '../services/api';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const AdminContactMessages = () => {
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, read, replied, archived

  useEffect(() => {
    fetchMessages();
  }, [filter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await contactAPI.getAll(params);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error(t('toast.failedToLoadMessages'), { autoClose: 1000 });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (messageId, newStatus) => {
    try {
      await contactAPI.updateStatus(messageId, newStatus);
      toast.success(t('toast.statusUpdated'), { autoClose: 1000 });
      fetchMessages();
    } catch (error) {
      toast.error(t('toast.statusUpdateFailed'), { autoClose: 1000 });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      read: 'bg-yellow-100 text-yellow-800',
      replied: 'bg-green-100 text-green-800',
      archived: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-display font-bold text-racing-black mb-2">
              {t('admin.contactMessages.title')}
            </h1>
            <p className="text-gray-600">
              {t('admin.contactMessages.subtitle')}
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {['all', 'new', 'read', 'replied', 'archived'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    filter === status
                      ? 'bg-racing-red text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t(`admin.contactMessages.filters.${status}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Messages List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-racing-red"></div>
              <p className="mt-4 text-gray-600">{t('admin.contactMessages.loading')}</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-gray-600 text-lg">{t('admin.contactMessages.noMessages')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                >
                  {/* Message Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-racing-black">
                          {message.subject}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            message.status
                          )}`}
                        >
                          {t(`admin.contactMessages.statuses.${message.status}`)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="font-semibold">{message.name}</span>
                        <span>•</span>
                        <a
                          href={`mailto:${message.email}`}
                          className="text-racing-red hover:underline"
                        >
                          {message.email}
                        </a>
                        {message.phone && (
                          <>
                            <span>•</span>
                            <a
                              href={`tel:${message.phone}`}
                              className="hover:underline"
                            >
                              {message.phone}
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      {formatDate(message.created_at)}
                    </div>
                  </div>

                  {/* Message Body */}
                  <div className="mb-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {message.message}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t">
                    <button
                      onClick={() => updateStatus(message.id, 'read')}
                      disabled={message.status === 'read'}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition text-sm font-medium"
                    >
                      {t('admin.contactMessages.buttons.markAsRead')}
                    </button>
                    <button
                      onClick={() => updateStatus(message.id, 'replied')}
                      disabled={message.status === 'replied'}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition text-sm font-medium"
                    >
                      {t('admin.contactMessages.buttons.markAsReplied')}
                    </button>
                    <button
                      onClick={() => updateStatus(message.id, 'archived')}
                      disabled={message.status === 'archived'}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition text-sm font-medium"
                    >
                      {t('admin.contactMessages.buttons.archive')}
                    </button>
                    <button
                      onClick={() => {
                        const subject = encodeURIComponent(`Re: ${message.subject}`);
                        const body = encodeURIComponent(`Hello ${message.name},\n\n`);
                        window.open(`https://interia.pl/?compose&to=${message.email}&subject=${subject}&body=${body}`, '_blank');
                      }}
                      className="ml-auto px-4 py-2 bg-racing-red text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                    >
                      {t('admin.contactMessages.buttons.replyViaEmail')}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminContactMessages;
